import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { getUser, hasSchemaACL, isWeaveUser } from '@frontend/auth-helpers';
import { useAppScopeStore } from '@frontend/scope';

interface UseHasAclAccessOptions {
  /** If true, user must have *all* permissions on a location; else just any one. */
  requireAllPermissions?: boolean;
  /** If true, check must pass for *every* location; else just any one. */
  requireAllLocations?: boolean;
  /** If provided, use these location IDs instead of the store’s selectedLocationIds. */
  locations?: string[];
  /** If true (default), internal Weave users bypass ACL checks. */
  allowWeaveOverride?: boolean;
  /** If true (default false), “demo” Weave users also bypass ACL checks. */
  allowDemoOverride?: boolean;
}

/**
 * Returns whether the current user has the given permissions
 * across their locations (or a custom list).
 */
export const useHasAclAccess = (
  permissions: Permission[],
  {
    requireAllPermissions = false,
    requireAllLocations = false,
    locations,
    allowWeaveOverride = true,
    allowDemoOverride = true,
  }: UseHasAclAccessOptions = {}
): boolean => {
  const { selectedLocationIds } = useAppScopeStore();
  const locationIds = locations ?? selectedLocationIds;
  const weaveUser = isWeaveUser();

  const username = getUser()?.username ?? '';
  const isDemoWeaveUser = username.toLowerCase().includes('@demoweave');

  return useMemo(() => {
    if (allowWeaveOverride && weaveUser) {
      return true;
    }
    if (allowDemoOverride && isDemoWeaveUser) {
      return true;
    }
    if (permissions.length === 0 || locationIds.length === 0) {
      return false;
    }

    // Check permissions on one location
    const doesUserPassLocation = (loc: string) => {
      if (requireAllPermissions) {
        return permissions.every((perm) => hasSchemaACL(loc, perm));
      }
      return permissions.some((perm) => hasSchemaACL(loc, perm));
    };

    // Finally, decide across locations
    if (requireAllLocations) {
      return locationIds.every(doesUserPassLocation);
    }
    return locationIds.some(doesUserPassLocation);
  }, [
    allowWeaveOverride,
    weaveUser,
    allowDemoOverride,
    isDemoWeaveUser,
    permissions,
    requireAllPermissions,
    requireAllLocations,
    locationIds,
  ]);
};
